import type { ConnectionState } from '../lib/connectivity';
import { CATALOG_STALE_MS } from './deviceCheck';
import type { PosStore } from './store';

/**
 * Artikelstand sichtbar machen (U17c, O1 bis O4): Wie aktuell ist der Stand auf diesem Gerät, lädt er gerade, ist er leer
 * oder veraltet? Die Bewertung ist rein. Dazu zwei stille Hilfen beim Öffnen der Kasse: ein alter Stand wird einmal
 * vollständig neu geladen (Cursor verwerfen), und der Browser wird um dauerhaften Speicher gebeten.
 */

/** Was der lokale Speicher über den Stand weiß. */
export interface StandInfo {
  /** Serverzeit des letzten vollständigen Abzugs (so stört eine falsch gehende Geräteuhr nicht); `null`, wenn noch keiner. */
  snapshotAt: Date | null;
  /** Zahl der Artikel auf dem Gerät. */
  items: number;
}

/** Ohne erfolgreichen Abzug länger als so lange (online) gilt der Stand als fraglich. */
export const NO_SUCCESS_MS = 10 * 60 * 1000;
/** Ist der Stand beim Öffnen älter und das Gerät online, wird er einmal vollständig neu geladen. */
export const FULL_RELOAD_MS = 12 * 60 * 60 * 1000;

export type StandHealth =
  | { kind: 'ok' }
  /** Der Stand ist leer und wird gerade geladen („6.000 geladen“). */
  | { kind: 'loading'; loaded: number }
  /** Kein Stand und kein Laden (offline): Verkaufen geht nur mit Preis von Hand. */
  | { kind: 'empty' }
  | { kind: 'stale'; reason: string };

export interface StandInput {
  stand: StandInfo | null;
  nowMs: number;
  connection: ConnectionState;
  /** Ein Sync-Lauf läuft gerade. */
  syncing: boolean;
  /** Letzter erfolgreicher Lauf (Uhr des Geräts, nur im Speicher). */
  lastSuccessAtMs: number | null;
  /** Bisher geladene Artikel (wächst Seite für Seite). */
  loaded: number;
}

export function standHealth(input: StandInput): StandHealth {
  const { stand } = input;
  if (stand === null) return { kind: 'ok' };
  if (stand.items === 0 && input.loaded === 0) {
    // Online, aber noch kein Lauf fertig oder einer läuft: es wird geladen. Sonst fehlt der Stand wirklich, auch bei
    // schwacher Verbindung (WLAN ohne Internet, `weak`): Dort käme sonst endlos „wird geladen“ ohne Grund.
    if (input.connection === 'online' && (input.syncing || input.lastSuccessAtMs === null)) {
      return { kind: 'loading', loaded: 0 };
    }
    return { kind: 'empty' };
  }
  if (stand.items === 0 || (input.syncing && stand.snapshotAt === null)) {
    return { kind: 'loading', loaded: Math.max(input.loaded, stand.items) };
  }
  if (stand.snapshotAt !== null && input.nowMs - stand.snapshotAt.getTime() > CATALOG_STALE_MS) {
    return { kind: 'stale', reason: 'Der Artikelstand ist älter als 24 Stunden.' };
  }
  if (
    input.connection === 'online' &&
    input.lastSuccessAtMs !== null &&
    input.nowMs - input.lastSuccessAtMs > NO_SUCCESS_MS
  ) {
    return {
      kind: 'stale',
      reason: 'Seit über 10 Minuten gab es online keinen erfolgreichen Abgleich.',
    };
  }
  return { kind: 'ok' };
}

/** Leiste über der Liste („Artikelstand wird geladen …“ bzw. der Grund für den leeren Stand). */
export function standBarText(health: StandHealth): string | null {
  if (health.kind === 'loading') {
    return health.loaded > 0
      ? `Artikelstand wird geladen … ${health.loaded.toLocaleString('de-DE')} geladen`
      : 'Artikelstand wird geladen …';
  }
  if (health.kind === 'empty') {
    return 'Kein Artikelstand auf diesem Gerät. Verkaufen geht nur mit Preis von Hand. Tipp: Kasse vor dem Basar einmal mit Netz öffnen.';
  }
  return null;
}

/** Liest Zeitpunkt und Zahl aus dem lokalen Speicher. */
export async function readStand(store: PosStore): Promise<StandInfo> {
  const text = await store.getMeta<string>('snapshotAt');
  const at = text === undefined ? null : new Date(text);
  return {
    snapshotAt: at !== null && !Number.isNaN(at.getTime()) ? at : null,
    items: await store.itemCount(),
  };
}

/**
 * Beim Öffnen: Ist der Stand älter als 12 Stunden, wird der Cursor verworfen, damit der nächste Abgleich ihn einmal
 * vollständig neu lädt. Das heilt ein Abdriften, ohne dass die Kasse wartet: Bis zum Ende gilt der alte Stand (die Seiten
 * ersetzen Artikel erst, wenn sie ankommen, der Cursor wird erst nach der letzten Seite gesetzt). Gibt zurück, ob verworfen wurde.
 */
export async function discardOldCursor(store: PosStore, nowMs: number): Promise<boolean> {
  const { snapshotAt, items } = await readStand(store);
  if (items === 0 || snapshotAt === null || nowMs - snapshotAt.getTime() <= FULL_RELOAD_MS) {
    return false;
  }
  await store.setMeta('cursor', undefined);
  return true;
}

interface StorageLike {
  persisted?: () => Promise<boolean>;
  persist?: () => Promise<boolean>;
}

/**
 * Bittet beim ersten Öffnen einmal still um dauerhaften Speicher (O4), wenn er noch nicht gewährt ist. Chromium und die
 * WebView der App fragen dabei nicht nach. Firefox würde nachfragen: dort nie ungefragt (es bleibt der Knopf im Geräte-Check).
 */
export async function persistQuietly(
  store: PosStore,
  storage: StorageLike | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.storage,
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): Promise<boolean> {
  if (/firefox|fxios/i.test(userAgent)) return false;
  if (storage?.persisted === undefined || storage.persist === undefined) return false;
  if ((await store.getMeta<boolean>('persistAsked')) === true) return false;
  if (await storage.persisted()) return false;
  await store.setMeta('persistAsked', true);
  await storage.persist();
  return true;
}
